import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';

const inp = "flex-1 border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";

const CondicoesPagamento = () => {
  const { isAdmin } = useAuth();
  const [condicoes, setCondicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adicionando, setAdicionando] = useState(false);
  const [novaDescricao, setNovaDescricao] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [editandoDescricao, setEditandoDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    try {
      const r = await api.get('/condicoes-pagamento');
      setCondicoes(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Erro ao carregar condições');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const handleAdicionar = async () => {
    if (!novaDescricao.trim()) return toast.error('Informe a descrição');
    setSalvando(true);
    try {
      await api.post('/condicoes-pagamento', { descricao: novaDescricao.trim(), ativo: true });
      toast.success('Condição criada!');
      setNovaDescricao('');
      setAdicionando(false);
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar condição');
    } finally {
      setSalvando(false);
    }
  };

  const handleEditar = async (id) => {
    if (!editandoDescricao.trim()) return toast.error('Informe a descrição');
    setSalvando(true);
    try {
      await api.put(`/condicoes-pagamento/${id}`, { descricao: editandoDescricao.trim(), ativo: true });
      toast.success('Condição atualizada!');
      setEditandoId(null);
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSalvando(false);
    }
  };

  const handleDeletar = async (id, descricao) => {
    if (!window.confirm(`Desativar "${descricao}"?`)) return;
    try {
      await api.delete(`/condicoes-pagamento/${id}`);
      toast.success('Condição desativada!');
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao desativar');
    }
  };

  const cancelarEdicao = () => { setEditandoId(null); setEditandoDescricao(''); };
  const cancelarAdicao = () => { setAdicionando(false); setNovaDescricao(''); };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#E9EEF2] p-6">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-lg font-black text-slate-900">Condições de Pagamento</h1>
            <p className="text-xs text-slate-400 font-semibold mt-0.5">
              Condições disponíveis nos formulários de pedidos, orçamentos e clientes
            </p>
          </div>
          {isAdmin && !adicionando && (
            <button
              onClick={() => setAdicionando(true)}
              className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 py-2.5 rounded-xl shadow-sm transition-colors">
              <Plus size={14} /> Nova Condição
            </button>
          )}
        </div>

        {/* Card principal */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

          {/* Formulário de adição */}
          {adicionando && (
            <div className="flex items-center gap-3 px-5 py-4 bg-blue-50 border-b border-slate-200">
              <input
                autoFocus
                value={novaDescricao}
                onChange={e => setNovaDescricao(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleAdicionar();
                  if (e.key === 'Escape') cancelarAdicao();
                }}
                placeholder="Ex: 30/60 DDL"
                className={inp} />
              <button
                onClick={handleAdicionar}
                disabled={salvando}
                className="flex items-center gap-1.5 bg-[#0A3D73] text-white text-xs font-black uppercase px-3 py-2 rounded-lg hover:bg-[#082D54] disabled:opacity-50 transition-colors whitespace-nowrap">
                <Check size={13} /> Salvar
              </button>
              <button
                onClick={cancelarAdicao}
                className="flex items-center gap-1.5 bg-slate-100 text-slate-600 text-xs font-black uppercase px-3 py-2 rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap">
                <X size={13} /> Cancelar
              </button>
            </div>
          )}

          {/* Lista vazia */}
          {condicoes.length === 0 && !adicionando && (
            <div className="py-16 text-center">
              <p className="text-sm font-bold text-slate-400">Nenhuma condição cadastrada</p>
              <p className="text-xs text-slate-300 mt-1">Clique em "Nova Condição" para começar</p>
            </div>
          )}

          {/* Lista de condições */}
          {condicoes.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {condicoes.map((c, i) => (
                <li key={c.id} className={`flex items-center gap-3 px-5 py-3.5 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                  {editandoId === c.id ? (
                    <>
                      <input
                        autoFocus
                        value={editandoDescricao}
                        onChange={e => setEditandoDescricao(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleEditar(c.id);
                          if (e.key === 'Escape') cancelarEdicao();
                        }}
                        className={inp} />
                      <button
                        onClick={() => handleEditar(c.id)}
                        disabled={salvando}
                        className="flex items-center gap-1 bg-emerald-500 text-white text-xs font-black uppercase px-2.5 py-1.5 rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-colors whitespace-nowrap">
                        <Check size={12} /> Salvar
                      </button>
                      <button
                        onClick={cancelarEdicao}
                        className="flex items-center gap-1 bg-slate-100 text-slate-600 text-xs font-black uppercase px-2.5 py-1.5 rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap">
                        <X size={12} /> Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm font-bold text-slate-700">{c.descricao}</span>
                      {isAdmin && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => { setEditandoId(c.id); setEditandoDescricao(c.descricao); }}
                            className="p-1.5 text-slate-400 hover:text-[#0A3D73] hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar">
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => handleDeletar(c.id, c.descricao)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Desativar">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Rodapé com contagem */}
          {condicoes.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
              <p className="text-xs font-bold text-slate-400">
                {condicoes.length} {condicoes.length !== 1 ? 'condições ativas' : 'condição ativa'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CondicoesPagamento;
